import { Download, Calendar, FileX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface Account {
  serialNo: number;
  accountNumber: string;
  accountAssociated: string;
  latestBillUrl: string | null;
  has12Months: boolean;
}

interface AccountsResultsCardProps {
  accounts: Account[];
  lastTested?: Date;
  testStatus?: 'Success' | 'Failed' | 'Processing';
  onDownloadLatest: (url: string) => void;
  on12MonthsClick: (account: Account) => void;
  userId?: string;
  portalName?: string;
  accountAssociated?: number;
}

export function AccountsResultsCard({
  accounts,
  lastTested,
  testStatus = 'Success',
  onDownloadLatest,
  on12MonthsClick,
  userId = '',
  portalName = '',
  accountAssociated = 0,
}: AccountsResultsCardProps) {
  // Always show the card structure, even for empty state

  return (
    <Card className="shadow-lg border-0 bg-gradient-to-br from-white to-green-50/30">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-xl">
          <span className="text-2xl">📊</span>
          Accounts Available
        </CardTitle>
        <CardDescription className="text-sm">
          {lastTested ? (
            <span>
              Last tested: {lastTested.toLocaleString()} • Status: 
              <span className={`ml-1 font-medium ${
                testStatus === 'Success' ? 'text-green-600' : 
                testStatus === 'Failed' ? 'text-red-600' : 
                'text-yellow-600'
              }`}>
                {testStatus}
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">
              Enter your portal credentials in the form on the right to test the Portal Bot
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* User Information Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 rounded-lg border border-blue-100">
          <div className="space-y-2">
            <Label htmlFor="user-id" className="text-blue-900 font-semibold">👤 User ID</Label>
            <Input
              id="user-id"
              value={userId || 'Enter credentials to test'}
              readOnly
              className="bg-white border-blue-200 focus:border-blue-400"
              placeholder="Username will appear here after test"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="portal-name" className="text-blue-900 font-semibold">🏢 Portal Name</Label>
            <Input
              id="portal-name"
              value={portalName || 'Select portal to test'}
              readOnly
              className="bg-white border-blue-200 focus:border-blue-400"
              placeholder="Portal name will appear here after test"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="account-associated" className="text-blue-900 font-semibold">📈 Account Associated</Label>
            <Input
              id="account-associated"
              value={accountAssociated > 0 ? accountAssociated.toString() : 'Test to see results'}
              readOnly
              className="bg-white border-blue-200 focus:border-blue-400"
              placeholder="Account count will appear here after test"
            />
          </div>
        </div>

        <div className="rounded-lg border border-blue-200 shadow-sm overflow-hidden">
          <Table>
            <TableHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
              <TableRow className="border-blue-200">
                <TableHead className="w-16 text-blue-900 font-semibold sticky left-0 bg-gradient-to-r from-blue-50 to-indigo-50 z-10">#</TableHead>
                <TableHead className="text-blue-900 font-semibold sticky left-16 bg-gradient-to-r from-blue-50 to-indigo-50 z-10">📋 Accounts Available</TableHead>
                <TableHead className="text-right w-48 text-blue-900 font-semibold">⚡ Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
                {accounts.length > 0 ? (
                  accounts.map((account) => (
                  <TableRow key={account.serialNo} className="hover:bg-blue-50/50 transition-colors">
                    <TableCell className="font-medium sticky left-0 bg-white z-10 border-r border-slate-100">{account.serialNo}</TableCell>
                    <TableCell className="font-mono text-sm sticky left-16 bg-white z-10 border-r border-slate-100">
                      {account.accountNumber}
                    </TableCell>
                  <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => account.latestBillUrl && onDownloadLatest(account.latestBillUrl)}
                          disabled={!account.latestBillUrl}
                          className="text-xs bg-blue-50 hover:bg-blue-100 border-blue-200 hover:border-blue-300 text-blue-700 hover:text-blue-800 transition-colors"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          📄 Latest Bill
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => on12MonthsClick(account)}
                          className="text-xs bg-green-50 hover:bg-green-100 border-green-200 hover:border-green-300 text-green-700 hover:text-green-800 transition-colors"
                        >
                          <Calendar className="h-3 w-3 mr-1" />
                          📅 12 Months
                        </Button>
                      </div>
                  </TableCell>
                </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} className="text-center py-16">
                    <div className="flex flex-col items-center">
                      <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center mb-6">
                        <span className="text-4xl">🚀</span>
                      </div>
                      <h3 className="text-xl font-bold text-blue-900 mb-3">Ready to Test Portal Bot?</h3>
                      <p className="text-base text-blue-700 max-w-lg leading-relaxed">
                        Use the form on the right side to enter your portal credentials. 
                        We suggest using accounts without 2FA and captcha enabled for easier testing.
                      </p>
                      <div className="mt-4 flex items-center gap-2 text-sm text-blue-600">
                        <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                        <span>Enter your credentials to get started</span>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
